import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import { api } from '../../../lib/api'
import { useAuthStore } from '../../../stores/auth'
import { Card } from '../../ui/Card'
import { Button } from '../../ui/Button'
import { SamplingWizard } from '../SamplingWizard'
import { ModalDetaliu, sePoateRelua } from '../../../pages/marketing/SamplingPage'
import { useConfirmare } from '../../ui/Confirmare'
import { eroare, zi, faraAcces, MESAJ_FARA_SAMPLING } from './comun'

/**
 * Acțiunile de teren ale activității — sesiunile cu cod QR.
 *
 * Nu le mutăm aici, le arătăm: formularul public, consimțământul și procesul-verbal trăiesc în
 * modulul de sampling și rămân acolo până la etapa 3. Ce lipsea era legătura în sensul ăsta —
 * dintr-un eveniment nu se putea vedea ce s-a întâmplat pe teren pentru el.
 */

interface ActiuneTeren {
  id: number
  denumire: string
  tip: string
  slug: string
  locatie: string | null
  dataStart: string | null
  activa: boolean
  inchisaLa: string | null
  nrPredari: number
  nrProduseActive: number
  agentName: string | null
}

export function FilaTeren({ campanieId }: { campanieId: number }) {
  const qc = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const poateScrie = permissions.includes('marketing.sampling:write')
  const poateSterge = permissions.includes('marketing.sampling:delete')
  const eAdmin = useAuthStore((s) => s.user?.role) === 'admin'
  const { confirma } = useConfirmare()
  /** Ce fel de acțiune se deschide: la eveniment se dau mostre, la tombolă se trage la sorți. */
  const [wizard, setWizard] = useState<'eveniment' | 'tombola' | null>(null)
  /*
    Panoul actiunii se deschide AICI, peste fila Teren.

    „Deschide" era un link catre lista de actiuni: te muta in alt meniu, iar la inchidere te
    lasa acolo, cu evenimentul pierdut (Alexandru, 22.09.2026).
  */
  const [detaliuId, setDetaliuId] = useState<number | null>(null)

  const q = useQuery<ActiuneTeren[]>({
    queryKey: ['campanie', campanieId, 'teren'],
    queryFn: async () =>
      (await api.get<ActiuneTeren[]>('/marketing/sampling/actiuni', { params: { campanieId } }))
        .data,
  })
  const actiuni = q.data ?? []

  /*
    Inchide, Duplica, Sterge — erau doar in Sampling → Actiuni (15.10.2026). Aceleasi rute si
    aceleasi reguli ca acolo: duplicarea si inchiderea doar adminii (le opreste si serverul),
    stergerea cu dreptul de stergere si doar fara predari. Inchiderea conteaza: la actiunile cu
    bon „la inchidere" abia ea emite bonul de consum.
  */
  const reimprospateaza = () => {
    void qc.invalidateQueries({ queryKey: ['campanie', campanieId] })
    void qc.invalidateQueries({ queryKey: ['sampling'] })
  }
  const inchide = useMutation({
    mutationFn: async (id: number) => api.post(`/marketing/sampling/actiuni/${id}/inchide`),
    onSuccess: () => {
      toast.success('Acțiune închisă')
      reimprospateaza()
    },
    onError: eroare,
  })
  const duplica = useMutation({
    mutationFn: async (id: number) =>
      (await api.post<{ id: number }>(`/marketing/sampling/actiuni/${id}/reporneste`)).data,
    onSuccess: (a) => {
      toast.success('Acțiune nouă, cu alt cod QR')
      reimprospateaza()
      setDetaliuId(a.id)
    },
    onError: eroare,
  })
  const sterge = useMutation({
    mutationFn: async (id: number) => api.delete(`/marketing/sampling/actiuni/${id}`),
    onSuccess: () => {
      toast.success('Acțiune ștearsă')
      reimprospateaza()
    },
    onError: eroare,
  })

  async function cereInchiderea(a: ActiuneTeren) {
    if (
      await confirma({
        titlu: `Închizi „${a.denumire}”?`,
        mesaj: 'Codul QR nu va mai funcționa. Dacă bonul se emite la închidere, pleacă acum.',
        buton: 'Închide acțiunea',
      })
    )
      inchide.mutate(a.id)
  }
  async function cereDuplicarea(a: ActiuneTeren) {
    if (
      await confirma({
        titlu: `Duplici „${a.denumire}”?`,
        mesaj:
          'Se face o acțiune NOUĂ, azi, cu aceeași configurare și alt cod QR. Afișele tipărite duc mai departe la acțiunea veche.',
        buton: 'Duplică',
      })
    )
      duplica.mutate(a.id)
  }
  async function cereStergerea(a: ActiuneTeren) {
    if (
      await confirma({
        titlu: `Ștergi definitiv „${a.denumire}”?`,
        mesaj: 'Nu are nicio predare, deci nu se pierde niciun document.',
        buton: 'Șterge',
        pericol: true,
      })
    )
      sterge.mutate(a.id)
  }

  /** Butoanele rare ale unei actiuni, aceleasi pe telefon si pe calculator. */
  const ButoaneActiune = ({ a }: { a: ActiuneTeren }) => (
    <>
      {poateScrie && eAdmin && sePoateRelua(a) && (
        <button
          type="button"
          onClick={() => void cereDuplicarea(a)}
          disabled={duplica.isPending}
          className="text-xs text-b4l-blue-dark hover:underline disabled:opacity-40"
          title="Acțiune nouă, azi, cu aceeași configurare și alt cod QR"
        >
          Duplică — cod QR nou
        </button>
      )}
      {poateScrie && eAdmin && !a.inchisaLa && (
        <button
          type="button"
          onClick={() => void cereInchiderea(a)}
          disabled={inchide.isPending}
          className="text-xs text-b4l-text-muted hover:text-b4l-red-dark disabled:opacity-40"
        >
          Închide
        </button>
      )}
      {poateSterge && a.nrPredari === 0 && (
        <button
          type="button"
          onClick={() => void cereStergerea(a)}
          disabled={sterge.isPending}
          className="text-xs text-b4l-red-dark hover:underline disabled:opacity-40"
        >
          Șterge
        </button>
      )}
    </>
  )

  if (faraAcces(q.error))
    return (
      <Card title="Acțiuni pe teren">
        <p className="text-sm text-b4l-text-muted">{MESAJ_FARA_SAMPLING}</p>
      </Card>
    )
  return (
    <>
      <Card
        title="Acțiuni pe teren"
        actions={
          poateScrie ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setWizard('tombola')}>
                Tombolă nouă
              </Button>
              <Button onClick={() => setWizard('eveniment')}>Acțiune nouă</Button>
            </div>
          ) : undefined
        }
      >
        {actiuni.length === 0 ? (
          <p className="text-sm text-b4l-text-muted">
            Nicio acțiune de teren pentru activitatea asta. „Acțiune nouă” deschide formularul cu
            evenimentul deja ales — de acolo ies codul QR și afișul.
          </p>
        ) : (
          <>
            {/*
              Pe telefon, un card pe actiune; de la `md` in sus, tabelul.

              Tabelul avea opt coloane si se folosea exact acolo unde nu incape: in dimineata
              evenimentului, de pe telefon, ca sa se gaseasca adresa formularului. Pe ecran de
              360 px denumirea cadea pe cinci randuri, iar „Predari", „Produse" si formularul QR
              ieseau in afara marginii (Alexandru, 22.09.2026).
            */}
            <ul className="space-y-2 md:hidden">
              {actiuni.map((a) => (
                <li key={a.id} className="rounded-lg border border-b4l-border p-3">
                  <div className="flex items-start gap-2">
                    <p className="flex-1 text-sm font-medium text-b4l-text">{a.denumire}</p>
                    <StareActiune a={a} />
                  </div>
                  <p className="mt-1 text-xs text-b4l-text-muted">
                    {zi(a.dataStart)}
                    {a.locatie ? ` · ${a.locatie}` : ''}
                    {a.agentName ? ` · ${a.agentName}` : ''}
                  </p>
                  <p className="mt-1 text-xs text-b4l-text-muted">
                    <span className="tabular-nums">{a.nrPredari}</span> predări ·{' '}
                    <span className="tabular-nums">{a.nrProduseActive}</span> produse
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <a
                      href={`/s/${a.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-xs text-b4l-blue-dark hover:underline"
                    >
                      /s/{a.slug}
                    </a>
                    <Link
                      to={`/marketing/sampling-stand?actiune=${a.id}`}
                      className="ml-auto rounded-lg bg-b4l-blue px-3 py-1.5 text-sm font-medium text-white"
                    >
                      Predare la stand
                    </Link>
                    <button
                      type="button"
                      onClick={() => setDetaliuId(a.id)}
                      className="text-sm text-b4l-blue-dark hover:underline"
                    >
                      Deschide
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3">
                    <ButoaneActiune a={a} />
                  </div>
                </li>
              ))}
            </ul>

            <table className="hidden w-full text-sm md:table">
              <thead className="text-left text-xs uppercase tracking-wide text-b4l-text-dim">
                <tr>
                  <th className="py-2">Denumire</th>
                  <th>Când</th>
                  <th>Unde</th>
                  <th>Agent</th>
                  <th className="text-right">Predări</th>
                  <th className="text-right">Produse</th>
                  <th>Formular QR</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {actiuni.map((a) => (
                  <tr key={a.id} className="border-t border-b4l-border">
                    <td className="py-2">
                      {a.denumire}
                      <span className="ml-2">
                        <StareActiune a={a} />
                      </span>
                    </td>
                    <td>{zi(a.dataStart)}</td>
                    <td>{a.locatie ?? '—'}</td>
                    <td>{a.agentName ?? '—'}</td>
                    <td className="text-right tabular-nums">{a.nrPredari}</td>
                    <td className="text-right tabular-nums">{a.nrProduseActive}</td>
                    <td>
                      {/*
                    Adresa pe care o scanează oamenii. Se vede aici fiindcă în dimineața
                    evenimentului asta se caută prima — nu deschizi un ecran ca să afli un link.
                  */}
                      <a
                        href={`/s/${a.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-mono text-xs text-b4l-blue-dark hover:underline"
                      >
                        /s/{a.slug}
                      </a>
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-3">
                        {/*
                          Predarea se face de aici, de pe telefonul consultantului: cauta omul,
                          alege produsul, acesta semneaza. Statea doar pe fisa de telefon —
                          Alexandru, 22.09.2026: „optiunea asta vreau sa fie disponibila aici pe
                          fiecare actiune", uitandu-se la tabelul de pe monitor.
                        */}
                        <Link
                          to={`/marketing/sampling-stand?actiune=${a.id}`}
                          className="text-b4l-blue-dark hover:underline"
                        >
                          Predare la stand
                        </Link>
                        <button
                          type="button"
                          onClick={() => setDetaliuId(a.id)}
                          className="text-b4l-blue-dark hover:underline"
                        >
                          Deschide
                        </button>
                      </div>
                      <div className="mt-1 flex justify-end gap-3">
                        <ButoaneActiune a={a} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>

      {detaliuId !== null && (
        <ModalDetaliu
          id={detaliuId}
          canWrite={poateScrie}
          onClose={() => {
            setDetaliuId(null)
            void qc.invalidateQueries({ queryKey: ['campanie', campanieId, 'teren'] })
          }}
        />
      )}

      {wizard && (
        <SamplingWizard
          tip={wizard}
          campanieInitiala={campanieId}
          onClose={() => setWizard(null)}
          onCreat={() => {
            setWizard(null)
            void qc.invalidateQueries({ queryKey: ['campanie', campanieId, 'teren'] })
          }}
        />
      )}
    </>
  )
}

/**
 * Starea actiunii, ca in Sampling → Actiuni. „Fara produse" e semnalul care conteaza: codul QR se
 * deschide pe „Actiune indisponibila", iar omul afla abia la stand. Tombola n-are produse — nu
 * se aplica.
 */
function StareActiune({ a }: { a: ActiuneTeren }) {
  if (a.inchisaLa)
    return (
      <span className="rounded-full bg-b4l-bg px-2 py-0.5 text-xs text-b4l-text-muted">
        închisă
      </span>
    )
  if (!a.activa)
    return (
      <span className="rounded-full bg-b4l-bg px-2 py-0.5 text-xs text-b4l-text-muted">oprită</span>
    )
  if (a.nrProduseActive === 0 && a.tip !== 'tombola')
    return (
      <span
        className="rounded-full bg-b4l-amber-light px-2 py-0.5 text-xs text-b4l-amber-dark"
        title="Bifează cel puțin un produs, altfel codul QR nu deschide formularul"
      >
        fără produse
      </span>
    )
  return null
}
